import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { nearestPoint, type Entry, type PatientSummary, type Range } from '@fatigue/shared';
import { listPatientEntries } from '../data/api';
import { FatigueChart } from './FatigueChart';
import { CommentList } from './CommentList';
import { EntryDetail } from './EntryDetail';
import { ResetAccess } from './ResetAccess';

interface Props {
  readonly patient: PatientSummary | null;
  /** Shown when there is no patient to show. Null while the roster is still
      loading or failed, where the sidebar is already saying so. */
  readonly emptyMessage: string | null;
  readonly selectedEntryId: string | null;
  readonly onSelectEntry: (entryId: string | null) => void;
}

const RANGES: readonly Range[] = [7, 28, 'course'];

export function PatientPanel({
  patient,
  emptyMessage,
  selectedEntryId,
  onSelectEntry,
}: Props): JSX.Element {
  const [range, setRange] = useState<Range>(28);
  const [entries, setEntries] = useState<readonly Entry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patientId = patient?.patientId ?? null;

  useEffect(() => {
    if (patientId === null) {
      setEntries([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    listPatientEntries(patientId, range)
      .then((loaded) => {
        if (!cancelled) setEntries(loaded);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load this record.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [patientId, range]);

  const stats = useMemo(() => computeStats(entries), [entries]);
  const selected = entries.find((entry) => entry.entryId === selectedEntryId) ?? null;

  if (patient === null) {
    return (
      <main className="panel">
        {emptyMessage !== null && <p className="empty">{emptyMessage}</p>}
      </main>
    );
  }

  return (
    <main className="panel">
      <div className="phead">
        <div>
          <h1>{patient.displayName}</h1>
          <p className="phead__sub">
            {patient.referenceCode} &middot; consented {formatDate(patient.consentedAt)}
          </p>
          {/* Keyed, so a half-finished confirmation never carries over to the
              next patient selected. */}
          <ResetAccess key={patient.patientId} patient={patient} />
        </div>
        <div className="ranges" role="group" aria-label="Time range">
          {RANGES.map((option) => (
            <button
              key={String(option)}
              type="button"
              className={`range${range === option ? ' range--on' : ''}`}
              aria-pressed={range === option}
              onClick={() => setRange(option)}
            >
              {option === 'course' ? 'Whole course' : `${option} days`}
            </button>
          ))}
        </div>
      </div>

      {error !== null && <p className="empty">{error}</p>}
      {loading && <p className="empty">Loading record&hellip;</p>}

      {!loading && error === null && (
        <>
          {stats !== null && (
            <div className="stats">
              <Stat
                label="Typical level"
                value={nearestPoint(stats.mean).name}
                note={`mean ${stats.mean.toFixed(1)}`}
                wordy
              />
              <Stat
                label="Change across period"
                value={describeDelta(stats.delta)}
                note="second half against first"
                wordy
              />
              <Stat label="Entries" value={String(stats.count)} note={`across ${stats.days} days`} />
              <Stat
                label="With a comment"
                value={String(stats.withComments)}
                note="click a ring to read"
              />
            </div>
          )}

          <section className="card">
            <FatigueChart
              entries={entries}
              selectedEntryId={selectedEntryId}
              onSelect={onSelectEntry}
            />
            <div className="legend">
              <span className="legend__item">
                <svg width="16" height="16" aria-hidden="true">
                  <circle cx="8" cy="8" r="4" fill="var(--ink-2)" />
                </svg>
                Entry
              </span>
              <span className="legend__item">
                <svg width="16" height="16" aria-hidden="true">
                  <circle cx="8" cy="8" r="5" fill="var(--panel)" stroke="var(--ink-2)" strokeWidth="2.5" />
                </svg>
                Has a comment
              </span>
              <span className="legend__item">
                <svg width="26" height="16" aria-hidden="true">
                  <line x1="1" y1="8" x2="25" y2="8" stroke="var(--muted)" strokeWidth="2" strokeDasharray="5 4" />
                </svg>
                Rolling mean of 5
              </span>
            </div>
          </section>

          <div className="panel__lower">
            <section className="card card--grow">
              <h2>Comments</h2>
              <CommentList
                entries={entries}
                selectedEntryId={selectedEntryId}
                onSelect={onSelectEntry}
              />
            </section>
            <section className="card card--side">
              <h2>Selected entry</h2>
              <EntryDetail entry={selected} />
            </section>
          </div>
        </>
      )}
    </main>
  );
}

function Stat({
  label,
  value,
  note,
  wordy = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly note: string;
  /** A name rather than a number, set smaller so it fits a quarter-width card. */
  readonly wordy?: boolean;
}): JSX.Element {
  return (
    <div className="stat">
      <span className="stat__label">{label}</span>
      <span className={`stat__value${wordy ? ' stat__value--wordy' : ''}`}>{value}</span>
      <span className="stat__note">{note}</span>
    </div>
  );
}

interface Stats {
  readonly mean: number;
  readonly delta: number;
  readonly count: number;
  readonly days: number;
  readonly withComments: number;
}

/**
 * Descriptive only. These are summaries of what was recorded, never a
 * judgement about it -- no thresholds, no flags, no recommendation. The
 * physiotherapist interprets, in session.
 */
function computeStats(entries: readonly Entry[]): Stats | null {
  if (entries.length === 0) return null;

  const mean = entries.reduce((sum, e) => sum + e.score, 0) / entries.length;
  const half = Math.floor(entries.length / 2);
  const first = entries.slice(0, half);
  const second = entries.slice(half);

  const average = (list: readonly Entry[]): number =>
    list.length === 0 ? 0 : list.reduce((sum, e) => sum + e.score, 0) / list.length;

  return {
    mean,
    delta: average(second) - average(first),
    count: entries.length,
    days: new Set(entries.map((e) => new Date(e.capturedAt).toDateString())).size,
    withComments: entries.filter((e) => e.comment !== '').length,
  };
}

/**
 * Neutral wording. "improving" describes the numbers, not the patient. A
 * quarter of a level either way reads as steady.
 */
function describeDelta(delta: number): string {
  if (delta < -0.2) return 'Lower';
  if (delta > 0.2) return 'Higher';
  return 'Steady';
}

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
